import { curry } from 'lodash'

export interface Calculated {
  arguments: [string, string]
  transformation_id: string
  origin_field_id: string
  func: string
  origin_field_type: string
  allow_reprocessing: boolean
  allow_reprocessing_only_for_inbound: boolean
}

export const initialState = {
  delimiter: '',
  position: '',
  originFieldId: '',
  selectableFields: [],
  allowReprocessing: false,
  allowReprocessingOnlyForInbound: false,
}

interface SplitState {
  delimiter: string
  position: string
  originFieldId: string
  selectableFields: any[]
  allowReprocessing: boolean
  allowReprocessingOnlyForInbound: boolean
}

export const SplitActionTypes = {
  // Split Transform Actions
  setDelimiter: 'setDelimiter',
  setPosition: 'setPosition',
  setOriginFieldId: 'setOriginFieldId',
  setSelectableFields: 'setSelectableFields',
  setExistingSplitTransform: 'setExistingSplitTransform',
  reset: 'reset',
  setAllowReprocessing: 'setAllowReprocessing',
  setAllowReprocessingOnlyForInbound: 'setAllowReprocessingOnlyForInbound',
} as const

export type SplitActionTypes = (typeof SplitActionTypes)[keyof typeof SplitActionTypes]

type SplitAction =
  | {
      type: typeof SplitActionTypes.setDelimiter
      payload: { delimiter: string }
    }
  | {
      type: typeof SplitActionTypes.setPosition
      payload: { position: string }
    }
  | {
      type: typeof SplitActionTypes.setOriginFieldId
      payload: { originFieldId: string }
    }
  | {
      type: typeof SplitActionTypes.setSelectableFields
      payload: { selectableFields: any[] }
    }
  | {
      type: typeof SplitActionTypes.reset
      payload: object
    }
  | {
      type: typeof SplitActionTypes.setExistingSplitTransform
      payload: {
        delimiter: string
        position: string
        originFieldId: string
        allowReprocessing: boolean
        allowReprocessingOnlyForInbound: boolean
      }
    }
  | {
      type: typeof SplitActionTypes.setAllowReprocessing
      payload: { allowReprocessing: boolean; allowReprocessingOnlyForInbound: boolean }
    }
  | {
      type: typeof SplitActionTypes.setAllowReprocessingOnlyForInbound
      payload: { allowReprocessingOnlyForInbound: boolean; allowReprocessing: boolean }
    }

export const SplitOriginGovernanceTypes = ['date', 'text']
const SplitDestinationGovernanceTypes = ['list', 'date', 'text']

const isAcceptedGovernanceCurry = curry((haystack: string[], needle: string) =>
  haystack.includes(needle),
)
const isSplitGovernanceTypeForOrigin = isAcceptedGovernanceCurry(SplitOriginGovernanceTypes)
export const isSplitGovernanceTypeForDestination = isAcceptedGovernanceCurry(
  SplitDestinationGovernanceTypes,
)
export const belongsToPattern = form => {
  if (form?.segment_precedence !== null && form?.segment_precedence !== undefined) {
    return true
  }

  return false
}

export function splitReducer(state: SplitState, action: SplitAction): SplitState {
  switch (action.type) {
    case 'setDelimiter': {
      const { delimiter } = action.payload
      return {
        ...state,
        delimiter,
      }
    }
    case 'setPosition': {
      const { position } = action.payload
      return {
        ...state,
        position,
      }
    }
    case 'setOriginFieldId': {
      const { originFieldId } = action.payload
      return {
        ...state,
        originFieldId,
      }
    }
    case 'setSelectableFields': {
      const filterSupportedGovTypes = selectFields =>
        selectFields?.filter(sf => isSplitGovernanceTypeForOrigin(sf.governance_type))
      const { selectableFields } = action.payload

      return {
        ...state,
        selectableFields: filterSupportedGovTypes(selectableFields),
      }
    }
    case 'setExistingSplitTransform': {
      const {
        position,
        delimiter,
        originFieldId,
        allowReprocessing,
        allowReprocessingOnlyForInbound,
      } = action.payload
      return {
        ...state,
        position,
        delimiter,
        originFieldId,
        allowReprocessing,
        allowReprocessingOnlyForInbound,
      }
    }
    case 'reset': {
      return {
        delimiter: '',
        position: '',
        selectableFields: [...state.selectableFields],
        originFieldId: '',
        allowReprocessing: false,
        allowReprocessingOnlyForInbound: false,
      }
    }
    case 'setAllowReprocessing': {
      const { allowReprocessing, allowReprocessingOnlyForInbound } = action.payload
      return {
        ...state,
        allowReprocessing,
        allowReprocessingOnlyForInbound,
      }
    }
    case 'setAllowReprocessingOnlyForInbound': {
      const { allowReprocessingOnlyForInbound, allowReprocessing } = action.payload
      return {
        ...state,
        allowReprocessingOnlyForInbound,
        allowReprocessing,
      }
    }
    default:
      return state
  }
}
